import {
  CSRF_HEADER,
  changeRequestState,
  changeSizes,
  type FolderId,
  headingAnchors,
  isFolderOp,
  type ProjectDetail,
  trustState,
  type UpdateWikiChangeItemRequest,
  WIKI_RESOLVE_ANSWERED_MAX,
  type WikiChangeItem,
  type WikiPageType,
  type WikiThreadKind,
} from "@esi/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Bot, Check, MessageSquare, MoreHorizontal, RotateCcw, X } from "lucide-react";
import { type ReactNode, type RefObject, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { DismissReasons } from "@/components/DismissReasons";
import { Markdown } from "@/components/markdown/Markdown";
import { NativeSelect } from "@/components/NativeSelect";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useMediaQuery } from "@/hooks/use-media-query";
import { csrfJson } from "../account/queries.ts";
import { api } from "../api.ts";
import { noContent, ok } from "../api-errors.ts";
import { formatDateTime } from "../i18n/format.ts";
import { t as i18nT } from "../i18n/i18n.ts";
import { canChange } from "../projects/capabilities.ts";
import { FolderField } from "../projects/FolderPicker.tsx";
import { useFolders } from "../projects/folders.ts";
import {
  changeSetPath,
  Link,
  navigate,
  projectConversationPath,
  useSearch,
  wikiChangesPath,
  wikiPath,
} from "../router.tsx";
import { useSession } from "../session.tsx";
import { AuthorPreview, authorPreviewOf, isStructureItem } from "./AuthorPreview.tsx";
import { ChangeSetPublishDialog } from "./ChangeSetPublishDialog.tsx";
import { DiffView } from "./DiffView.tsx";
import { SinceReviewLine, useReviewSince } from "./history.tsx";
import { ItemWarnings } from "./ItemWarnings.tsx";
import { MarkedPage, type ReadingView, readingViewOf, shownPage } from "./MarkedPage.tsx";
import { useChangeItem, useChangeSet, useReviewQueue, useWikiPage, wikiKeys } from "./queries.ts";
import { ResolveStep } from "./ResolveNote.tsx";
import { RestoreDialog, type RestoreTarget } from "./RestoreDialog.tsx";
import { type Dock, ReviewPane, ReviewSheet } from "./ReviewPane.tsx";
import {
  anchorOf,
  DecisionBar,
  entryOfFlag,
  entryOfThread,
  type Flag,
  IssueLine,
  IssueLines,
  IssueMarker,
  type ItemDecision,
  isBlocking,
  isContentReadable,
  jumpTo,
  outlineGroupOf,
  type ReviewEntry,
  STRUCTURE,
  usePassageHighlights,
} from "./ReviewReader.tsx";
import { ReviewStrip, type ReviewTab } from "./ReviewStrip.tsx";
import { answeredThreads, countsText, useResolveAnswered } from "./resolve.ts";
import { type PassageAnchor, type Placement, placeAnchors } from "./review-anchors.ts";
import { useReviewPref } from "./review-prefs.ts";
import { SelectionPopover } from "./SelectionPopover.tsx";
import { rangeOf, sectionSlug } from "./selection.ts";
import { itemSize, sizesText } from "./sizes.ts";
import { NewThread, ThreadPanel } from "./ThreadPanel.tsx";
import { useThreads, useThreadWrite } from "./threads.ts";
import { useReviewGuard } from "./use-review-guard.tsx";

export function ChangeSetReview({ project, id }: { project: ProjectDetail; id: string }) {
  const { t } = useTranslation();
  const set = useChangeSet(project.id, id);
  const threads = useThreads(project.id, id);
  const write = useThreadWrite(project.id, id);
  const domId = useId();
  const wide = useMediaQuery("(min-width: 1024px)");
  const search = useSearch();
  const selectedParam = search.get("item");
  const view = readingViewOf(search.get("view")) ?? "marked";
  const threadParam = search.get("thread");
  const queryClient = useQueryClient();
  const { csrfToken, userId } = useSession();
  const [dialog, setDialog] = useState<"request" | "discard" | "publish" | null>(null);
  // "What changed since my last review" (S5, D18): the viewer's own marks, when they have any.
  const sinceReview = useReviewSince(project.id, id, true);
  // `since=1`: a Changed agent reply's "See the change" (wiki-resolve-with-agent D7, amended).
  const [sinceOn, setSinceOn] = useState(() => search.get("since") === "1");
  // Resolve answered (wiki-resolve-with-agent D5): the batch resolve and its note step.
  const resolveAnswered = useResolveAnswered(project.id, id);
  const [answering, setAnswering] = useState(false);
  const runParam = search.get("run");
  const sinceParam = search.get("since") === "1";
  useEffect(() => {
    // "See the change" from a thread on this review (the route stays mounted).
    if (sinceParam) {
      setSinceOn(true);
    }
  }, [sinceParam]);
  // The companion pane (D15): the open item, each item's unsent reply, the sheet's height, and
  // the person's Show issues inline and dock choices.
  const [openKey, setOpenKey] = useState<string | null>(threadParam);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [sheetOpen, setSheetOpen] = useState(true);
  const [inlinePref, setInlinePref] = useReviewPref("inline", userId, ["on", "off"] as const);
  const [dockPref, setDockPref] = useReviewPref("dock", userId, ["side", "bottom"] as const);
  const opener_ = useRef<HTMLElement | null>(null);
  const focusPane = useRef(false);
  const focusDock = useRef(false);
  const paneTitle = useRef<HTMLHeadingElement>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const tab: ReviewTab = search.get("show") === "discussion" ? "discussion" : "changes";
  const [revert, setRevert] = useState<RestoreTarget | null>(null);
  const revertTrigger = useRef<HTMLButtonElement>(null);
  const [comment, setComment] = useState("");
  // An N-1 API refused the discard's comment (400): the set was discarded without it.
  const [commentDropped, setCommentDropped] = useState(false);
  const commentDroppedRef = useRef(false);
  // "Approve and publish" left these pages pending (a change request, the viewer's own edits).
  const [skipped, setSkipped] = useState(0);
  const [skippedOwn, setSkippedOwn] = useState(0);
  // The agent's item just rejected here: Reject stays one click, then "Why?" is offered.
  const [rejected, setRejected] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [unsavedText, setUnsavedText] = useState("");
  const guard = useReviewGuard(dirty, unsavedText);
  const page = useRef<HTMLDivElement>(null);
  // Approve and publish: the confirmation opens once the approvals landed.
  const publishAfter = useRef(false);
  const refresh = () => queryClient.invalidateQueries({ queryKey: wikiKeys.all(project.id) });
  const param = { projectId: project.id, id };
  const headers = { [CSRF_HEADER]: csrfToken };
  const endpoint = api.api.projects[":projectId"].wiki["change-sets"][":id"];
  const value = set.data;
  const own = !!value && authorPreviewOf(value, userId);
  const open = !!value && (value.status === "open" || value.status === "changes_requested");
  // Possible contradictions ride in the review queue (D7): only for viewers who may read it.
  const queue = useReviewQueue(
    project.id,
    !!value && open && !own && canChange(project, "wiki.view_drafts"),
  );
  const content = value?.items.filter((i) => outlineGroupOf(i) !== "structure") ?? [];
  const structure = value?.items.filter((i) => outlineGroupOf(i) === "structure") ?? [];
  const selectedId =
    selectedParam === STRUCTURE && structure.length
      ? STRUCTURE
      : (value?.items.find((i) => i.id === selectedParam)?.id ??
        (selectedParam
          ? undefined
          : (content[0]?.id ?? (structure.length ? STRUCTURE : undefined))));
  const structureMember = structure.some((i) => i.id === selectedId);
  const shownId = structureMember ? STRUCTURE : selectedId;
  const selected = value?.items.find((i) => i.id === shownId);
  const item = useChangeItem(project.id, id, selected?.id);
  const action = useMutation({
    mutationFn: async ({
      kind,
      itemId,
    }: {
      kind: "approve" | "reject" | "reopen" | "regenerate" | "approveAll" | "discard";
      itemId?: string;
    }) => {
      if (kind === "approveAll") {
        const res = await endpoint.approve.$post({ param }, { headers });
        // Items under an open change request stay pending (an N-1 API answers 204).
        const body = (res.status as number) === 204 ? null : await ok(res);
        setSkipped(body?.skippedChangeRequested ?? 0);
        setSkippedOwn(body?.skippedOwnEdits ?? 0);
        return;
      }
      if (kind === "discard") {
        const text = comment.trim();
        if (!text) {
          return noContent(await endpoint.discard.$post({ param }, { headers }));
        }
        const res = await endpoint.discard.$post({ param, json: { comment: text } }, { headers });
        // An N-1 API ignores the body and answers 204: the set is discarded without the comment.
        if ((res.status as number) === 204) {
          commentDroppedRef.current = true;
          setCommentDropped(true);
          return;
        }
        if (!res.ok) {
          await noContent(res as never);
        }
        return;
      }
      const p = { ...param, itemId: itemId ?? "" };
      const route = endpoint.items[":itemId"];
      if (kind === "regenerate") {
        await ok(await route.regenerate.$post({ param: p }, { headers }));
        return;
      }
      return noContent(await route[kind].$post({ param: p }, { headers }));
    },
    onSuccess: (_, variables) => {
      if (variables.kind !== "approveAll") {
        setSkipped(0);
        setSkippedOwn(0);
      }
      setRejected(
        variables.kind === "reject" && value?.origin === "agent"
          ? (variables.itemId ?? null)
          : null,
      );
      // Approve and publish: the approvals land, then the publish confirmation (D6).
      setDialog(variables.kind === "approveAll" && publishAfter.current ? "publish" : null);
      setComment("");
      void refresh();
      if (variables.kind === "discard" && !commentDroppedRef.current) {
        navigate(wikiChangesPath(project.id));
      }
    },
    onError: () => {
      void refresh();
    },
  });
  // The deep link keeps the selection: the first page is written into the URL on a wide screen,
  // keeping the other parameters. A thread link waits for its thread, which picks its own page.
  const threadTarget = threads.data?.find((th) => th.id === threadParam);
  const threadPending = !!threadParam && (threads.isPending || !!threadTarget);
  useEffect(() => {
    if (!own && !selectedParam && selectedId && wide && value && !threadPending) {
      const url = new URL(window.location.href);
      url.searchParams.set("item", selectedId);
      navigate(`${url.pathname}${url.search}`, { replace: true });
    }
  }, [own, selectedParam, selectedId, wide, value, threadPending]);
  // A thread link opens on its item, or in Discussion for a set-level thread (web-shell "Thread
  // deep links"); unknown ones say nothing.
  // Once per link: closing the pane, stepping or leaving Discussion is not undone.
  const handledThread = useRef<string | null>(null);
  useEffect(() => {
    const target = threads.data?.find((th) => th.id === threadParam);
    if (!target || own || handledThread.current === target.id) {
      return;
    }
    if (!target.itemId || target.itemId === selectedParam) {
      handledThread.current = target.id;
    }
    if (target.itemId && target.itemId !== selectedParam) {
      navigate(`${changeSetPath(project.id, id, target.itemId)}&thread=${target.id}`, {
        replace: true,
      });
    } else if (!target.itemId && search.get("show") !== "discussion") {
      const url = new URL(window.location.href);
      url.searchParams.set("show", "discussion");
      navigate(`${url.pathname}${url.search}`, { replace: true });
    }
    setOpenKey(target.id);
  }, [threads.data, threadParam, selectedParam, own, project.id, id, search]);
  // Opening an item moves focus to it; a dock switch keeps focus on the switch (D14).
  useEffect(() => {
    if (focusPane.current && openKey) {
      focusPane.current = false;
      paneTitle.current?.focus();
    }
  });
  // Escape closes the open item wherever focus is on the page, unless a dialog or the selection
  // popover holds it (D14); focus returns to the line, marker or step that opened it.
  const closeOnEscape = useRef<() => void>(() => {});
  useEffect(() => {
    if (!openKey) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      const target = e.target instanceof Element ? e.target : null;
      if (
        e.key === "Escape" &&
        !e.defaultPrevented &&
        !target?.closest("[role='dialog'], [role='menu'], [data-popover]") &&
        !document.querySelector("[role='dialog'][data-state='open']")
      ) {
        e.preventDefault();
        closeOnEscape.current();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openKey]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs when the dock changes.
  useEffect(() => {
    if (focusDock.current) {
      focusDock.current = false;
      document.querySelector<HTMLElement>("[data-dock-switch]")?.focus();
    }
  }, [dockPref]);
  if (set.isPending) {
    return <Skeleton className="m-5 h-48" />;
  }
  if (set.isError) {
    return (
      <div data-esi-content="wiki" className="p-5">
        <Notice tone="danger">{set.error.message}</Notice>
        <Link href={wikiChangesPath(project.id)} className="mt-4 block text-accent-text">
          {t("wiki.changes.review")}
        </Link>
      </div>
    );
  }
  const data = set.data;
  const setView = (next: ReadingView) => {
    const url = new URL(window.location.href);
    url.searchParams.set("view", next);
    navigate(`${url.pathname}${url.search}`, { replace: true });
  };
  // The creator's own open set, whatever its origin, reads as their preview (spec `project-wiki`
  // "The author's own set shows no reviewer actions"): author actions only.
  if (own) {
    const ownSelected =
      data.items.find((i) => i.id === selectedParam) ??
      (selectedParam ? undefined : data.items.find((i) => !isStructureItem(i)));
    return (
      <div className="mx-auto min-h-0 w-full max-w-4xl flex-1 overflow-y-auto">
        <AuthorPreview
          project={project}
          set={data}
          selected={selectedParam}
          onSelect={(next) => navigate(changeSetPath(project.id, id, next), { replace: true })}
          view={view}
          onView={setView}
          inConversation={false}
          runId={runParam}
          onRun={(run) => {
            const url = new URL(window.location.href);
            if (run) {
              url.searchParams.set("run", run);
            } else {
              url.searchParams.delete("run");
            }
            navigate(`${url.pathname}${url.search}`, { replace: true });
          }}
        />
        <div className="flex flex-col gap-4 px-4 pb-6 sm:px-5">
          {ownSelected ? (
            <ThreadPanel
              projectId={project.id}
              set={data}
              itemId={ownSelected.id}
              canComment={canChange(project, "wiki.view_drafts")}
            />
          ) : null}
          <ThreadPanel
            projectId={project.id}
            set={data}
            itemId={null}
            canComment={canChange(project, "wiki.view_drafts")}
          />
        </div>
      </div>
    );
  }
  if (selectedParam && !selectedId) {
    return (
      <div className="flex flex-col gap-4 p-5">
        <Notice>{t("wiki.changes.notFound")}</Notice>
        <Link
          href={changeSetPath(project.id, id)}
          className="text-sm text-accent-text hover:underline"
        >
          {t("wiki.changes.allPages")}
        </Link>
      </div>
    );
  }
  const closed = data.status === "published" || data.status === "discarded";
  // A working change set (wiki-collaboration D8): open, not yet submitted by its creator, who
  // alone takes decisions on it until then.
  const working = data.status === "open" && data.submittedAt === null;
  const mine = data.createdBy.toLowerCase() === userId.toLowerCase();
  const othersWorking = working && !mine;
  // Decisions need `wiki.draft` on an open set: a reader, a guest or a closed set gets none.
  const canWrite = canChange(project, "wiki.draft") && !closed && !othersWorking;
  // Threads: any `wiki.view_drafts` holder may comment (D14).
  const canComment = canChange(project, "wiki.view_drafts") && !closed;
  const canPublish = (data.mayPublish ?? canChange(project, "wiki.publish")) && !closed;
  const stale = data.items.filter((i) => i.status === "stale");
  // The author of an MCP proposal they may not publish (#152): someone else approves.
  const approves = !(mine && data.mcpPublisher === "others");
  const changeRequests = changeRequestState(data);
  const trust = trustState(data);
  const decides = canWrite && approves && trust.decides;
  // The ink action (D6): Approve and publish for a decider who may publish the whole set,
  // Approve when someone else publishes, none for a viewer the trust level refuses.
  const ink: "publish" | "approve" | null = !decides
    ? null
    : canPublish && trust.whole
      ? "publish"
      : "approve";
  const live = data.items.filter((i) => i.status === "pending" || i.status === "approved");
  const inkDisabled =
    data.hiddenItems ||
    stale.length > 0 ||
    (ink === "publish"
      ? changeRequests.publishBlocked || !live.length
      : !live.some((i) => i.status === "pending"));
  const flags = (queue.data?.items ?? []).filter(
    (r) => r.kind === "contradiction" && data.items.some((i) => i.id === r.changeItemId),
  );
  const flagCount = new Map<string, number>();
  for (const f of flags) {
    if (f.changeItemId) {
      flagCount.set(f.changeItemId, (flagCount.get(f.changeItemId) ?? 0) + 1);
    }
  }
  const select = (next: string) =>
    guard.confirm(() => {
      setEditing(false);
      setDirty(false);
      setOpenKey(null);
      const url = changeSetPath(project.id, id, next);
      navigate(search.get("view") ? `${url}&view=${search.get("view")}` : url);
    });
  const setTab = (next: ReviewTab) => {
    const url = new URL(window.location.href);
    if (next === "discussion") {
      url.searchParams.set("show", "discussion");
    } else {
      url.searchParams.delete("show");
    }
    setOpenKey(null);
    navigate(`${url.pathname}${url.search}`, { replace: true });
  };
  // Per-item decisions in the pill's menu, only for a set of several items (Q9).
  const decisions = (entry: WikiChangeItem): ItemDecision[] =>
    data.items.length < 2 ? [] : decisionsOn(entry);
  const decisionsOn = (entry: WikiChangeItem): ItemDecision[] => {
    // Nothing is decided while the inline editor holds unsaved text (it would decide the old).
    if (!decides || editing) {
      return [];
    }
    const out: ItemDecision[] = [];
    if (
      entry.status === "pending" &&
      approves &&
      trust.item(entry.id) &&
      !changeRequests.onItem(entry.id)
    ) {
      out.push("approve");
    }
    if (entry.status === "pending" || entry.status === "approved") {
      out.push("reject");
    }
    if (entry.status === "rejected") {
      out.push("reopen");
    }
    return out;
  };
  // A single page has no pill menu: its rejection is undone from the summary menu, under the
  // pill menu's rules, so a rejected move or deletion is not left with Discard alone.
  const single = data.items.length === 1 ? data.items[0] : undefined;
  const reopenable = single && decisionsOn(single).includes("reopen") ? single : null;
  const title = (data.hiddenItems ? "" : data.title) || t("wiki.changes.hiddenTitle");
  const sizes = sizesText(changeSizes(data));
  const setThreads = (threads.data ?? []).filter((th) => th.itemId === null);
  const setRequests = setThreads.filter(
    (th) => th.kind === "change_request" && th.status === "open",
  ).length;
  const readable = !!item.data && isContentReadable(item.data) && !editing;
  const headingPrefix = `review-${(selected?.id ?? "").slice(0, 8)}`;
  // The set's creator talks to their agent in the conversation itself (D15).
  const agentEligible = data.origin === "agent" && !data.provenance.conversationDeleted && !mine;
  // The page's items (D15): its possible contradictions, then its threads.
  const itemFlags = flags.filter((f) => f.changeItemId === selected?.id);
  const entries: ReviewEntry[] =
    readable && selected
      ? [
          ...itemFlags.map((f) => entryOfFlag(f, item.data?.markdown)),
          ...(threads.data ?? []).filter((th) => th.itemId === selected.id).map(entryOfThread),
        ]
      : [];
  // With "since my last review" on, a revised item is marked against the text the viewer decided
  // on; anything else against its base version as usual.
  const sinceItem = sinceOn
    ? sinceReview.data?.items.find((i) => i.itemId === selected?.id)
    : undefined;
  const reviewedBase =
    sinceItem?.state === "revised" ? (sinceItem.reviewed?.markdown ?? null) : null;
  const baseMarkdown =
    reviewedBase ?? (item.data?.op === "edit" ? (item.data.baseMarkdown ?? null) : null);
  const shown =
    readable && item.data
      ? shownPageOnce(baseMarkdown, item.data.markdown ?? "", view, item.data.title)
      : null;
  const anchors = entries.map(anchorOf);
  const placements = shown?.markdown
    ? placeAnchorsOnce(
        shown.markdown,
        anchors.map((a) => a ?? { quote: "", prefix: "", suffix: "" }),
        shown.skip,
        shown.omit,
      )
    : [];
  const placed = entries.map((entry, i) => ({ entry, at: placements[i] ?? null }));
  // The stepper's order: unplaced first (they sit above the page), then down the page.
  const openItems = placed
    .filter((p) => isBlocking(p.entry))
    .sort(
      (a, b) =>
        (a.at ? a.at.block : -1) - (b.at ? b.at.block : -1) || (a.at?.at ?? -1) - (b.at?.at ?? -1),
    )
    .map((p) => p.entry);
  const inline = inlinePref ? inlinePref === "on" : decides;
  const current = tab === "changes" ? (entries.find((e) => e.key === openKey) ?? null) : null;
  const currentAnchor = current ? anchorOf(current) : null;
  const currentPlaced = current
    ? (placed.find((p) => p.entry.key === current.key)?.at ?? null)
    : null;
  const stepIndex = current ? openItems.findIndex((e) => e.key === current.key) : -1;
  const layout: "side" | "sheet" = wide && dockPref !== "bottom" ? "side" : "sheet";
  const openEntry = (key: string, opener?: HTMLElement | null) => {
    opener_.current = opener ?? (document.activeElement as HTMLElement | null);
    setOpenKey(key);
    setSheetOpen(true);
    focusPane.current = true;
  };
  const step = (direction: -1 | 1) => {
    if (!openItems.length) {
      return;
    }
    const at = stepIndex < 0 ? (direction > 0 ? -1 : 0) : stepIndex;
    const next = openItems[(at + direction + openItems.length) % openItems.length];
    if (next) {
      openEntry(next.key);
    }
  };
  const closePane = () => {
    setOpenKey(null);
    const back = opener_.current;
    opener_.current = null;
    if (back?.isConnected) {
      back.focus();
    }
  };
  closeOnEscape.current = closePane;
  const marker = (entry: ReviewEntry) => (
    <IssueMarker
      key={entry.key}
      entry={entry}
      open={current?.key === entry.key}
      onOpen={() => openEntry(entry.key)}
    />
  );
  const line = (entry: ReviewEntry) => (
    <IssueLine
      key={entry.key}
      entry={entry}
      open={current?.key === entry.key}
      onOpen={() => openEntry(entry.key)}
    />
  );
  // Calm text (D15): blocking items as lines under their passage while Show issues inline is on,
  // every other item a count marker at the end of its sentence; unplaced items above the page.
  const inlineAt = new Map<number, ReactNode[]>();
  const blockAfter = new Map<number, { lines: ReviewEntry[]; markers: ReviewEntry[] }>();
  const unplaced: ReviewEntry[] = [];
  // Down the page: lines under one passage read in the order of their sentences.
  const byPosition = [...placed].sort((a, b) => (a.at?.at ?? -1) - (b.at?.at ?? -1));
  for (const { entry, at } of byPosition) {
    const asLine = inline && isBlocking(entry);
    if (!at) {
      unplaced.push(entry);
    } else if (!asLine && at.at !== null) {
      inlineAt.set(at.at, [...(inlineAt.get(at.at) ?? []), marker(entry)]);
    } else {
      const slot = blockAfter.get(at.block) ?? { lines: [], markers: [] };
      (asLine ? slot.lines : slot.markers).push(entry);
      blockAfter.set(at.block, slot);
    }
  }
  const blockNodes = new Map<number, ReactNode>(
    [...blockAfter].map(([block, slot]) => [
      block,
      <IssueLines key={`b${block}`}>
        {slot.lines.map(line)}
        {slot.markers.length ? <p>{slot.markers.map(marker)}</p> : null}
      </IssueLines>,
    ]),
  );
  const unplacedLines = unplaced.filter((e) => inline && isBlocking(e));
  const unplacedMarkers = unplaced.filter((e) => !(inline && isBlocking(e)));
  const status = [
    ...(changeRequests.publishBlocked && decides ? [t("wiki.changes.publishBlocked")] : []),
    ...(setRequests ? [t("wiki.review.bar.setRequests", { count: setRequests })] : []),
    ...(flags.length && ink ? [t("wiki.review.bar.acknowledges", { count: flags.length })] : []),
    ...(stale.length ? [t("wiki.review.bar.stale", { count: stale.length })] : []),
    ...(skipped > 0 ? [t("wiki.changes.skipped", { count: skipped })] : []),
    ...(skippedOwn > 0 ? [t("wiki.changes.skippedOwn", { count: skippedOwn })] : []),
    ...(trust.ownEdits && canWrite ? [t("wiki.changes.trustOwnEdits")] : []),
  ].join(" · ");
  // The heading a thread's passage sits under, from the page's Markdown (never the DOM, which
  // holds markers and may not be mounted yet).
  const sectionOf = (entry: ReviewEntry | null) => {
    const slug = entry?.type === "thread" ? entry.thread.anchor?.section : null;
    return slug && item.data?.markdown
      ? (headingAnchors(item.data.markdown).find((h) => h.slug === slug)?.text ?? null)
      : null;
  };
  const postThread = async (request: Parameters<typeof write.mutateAsync>[0]) => {
    const before = new Set((threads.data ?? []).map((th) => th.id));
    const after = await write.mutateAsync(request);
    // The new thread opens in the pane, anchored to its passage (D12).
    const created = after.find((th) => !before.has(th.id));
    if (created) {
      openEntry(created.id);
    }
    return after;
  };
  // The agent's revision (wiki-resolve-with-agent D7): its counts once sent, "not sent yet" while
  // its replies are drafts; and the threads the viewer may resolve at once (D5).
  const revision = data.agentRevision;
  const lead =
    revision && (revision.status === "sent" || revision.status === "drafted") ? (
      <span data-agent-revision={revision.status} className="flex items-center gap-1.5">
        <Bot aria-hidden="true" className="size-3.5 shrink-0" />
        {revision.status === "sent"
          ? t("wiki.resolve.bar.sent", {
              revision: revision.revision,
              counts: countsText(revision.counts),
            })
          : t("wiki.resolve.bar.drafted", { revision: revision.revision })}
      </span>
    ) : null;
  // At most one request's worth (WIKI_RESOLVE_ANSWERED_MAX); the rest stay for the next one.
  const answered =
    open && !editing ? answeredThreads(threads.data ?? []).slice(0, WIKI_RESOLVE_ANSWERED_MAX) : [];
  const menu = answered.length ? (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t("wiki.resolve.bar.menu")}
          disabled={resolveAnswered.isPending}
        >
          <MoreHorizontal aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" side="top" className="w-72">
        <DropdownMenuItem
          className="flex-col items-start gap-0.5"
          onSelect={() => {
            resolveAnswered.reset();
            setAnswering(true);
          }}
        >
          <span className="flex items-center gap-2">
            <Check aria-hidden="true" />
            {t("wiki.resolve.bar.resolveAnswered", { count: answered.length })}
          </span>
          <span className="pl-6 text-xs text-fg-muted">
            {t("wiki.resolve.bar.resolveAnsweredHint")}
          </span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  ) : null;
  const bar = (
    <DecisionBar
      lead={lead}
      menu={menu}
      status={status || null}
      // The bar is a decider's (D6): a reader, a guest or a refused viewer gets none, and
      // still comments or requests a change on a passage.
      request={decides && open && !editing}
      ink={open && !editing ? ink : null}
      inkDisabled={inkDisabled}
      inkDescribedBy={status ? `${domId}-status` : undefined}
      busy={action.isPending}
      inSheet={layout === "sheet"}
      onRequest={() => {
        setComment("");
        setDialog("request");
      }}
      onInk={() => {
        publishAfter.current = ink === "publish";
        action.mutate({ kind: "approveAll" });
      }}
    />
  );
  const paneItem = current
    ? {
        projectId: project.id,
        setId: id,
        entry: current,
        pageTitle: item.data ? itemName(item.data) : "",
        section: sectionOf(current),
        onJump:
          currentAnchor && currentPlaced
            ? () => {
                if (layout === "sheet") {
                  setSheetOpen(false);
                }
                jumpTo(page.current, currentAnchor);
              }
            : null,
        draft: drafts[current.key] ?? "",
        onDraft: (text: string) => setDrafts((d) => ({ ...d, [current.key]: text })),
        canComment,
        agentEligible,
        onSeeChange: () => {
          // The page's change against the viewer's last review when they have marks, else
          // against the base (wiki-resolve-with-agent D7, amended), at the thread's passage.
          if (sinceReview.data?.items.some((i) => i.itemId === selected?.id)) {
            setSinceOn(true);
          }
          if (view !== "marked") {
            setView("marked");
          }
          if (layout === "sheet") {
            setSheetOpen(false);
          }
          if (currentAnchor) {
            requestAnimationFrame(() => jumpTo(page.current, currentAnchor));
          }
        },
        onFlagThread: (
          flag: Flag,
          kind: "comment" | "change_request",
          body: string,
          forAgent: boolean,
        ) =>
          postThread({
            kind: "open",
            request: {
              itemId: selected?.id ?? "",
              kind,
              body,
              ...(forAgent && { forAgent: true }),
              // Anchored only when the flagged sentence reads on the page (the model may have
              // reworded it): else a thread on the page as a whole.
              ...(page.current &&
                rangeOf(page.current, flag.statement) && {
                  anchor: {
                    quote: flag.statement.slice(0, 500),
                    prefix: "",
                    suffix: "",
                    section: null,
                  },
                }),
            },
          }),
      }
    : null;
  const frame = {
    step: stepIndex >= 0 ? { index: stepIndex, count: openItems.length } : null,
    onStep: step,
    onClose: closePane,
    dockable: wide,
    onDock: (next: Dock) => {
      focusDock.current = true;
      setDockPref(next);
    },
    titleRef: paneTitle,
  };
  // Discussion (D15): set-level threads in place, and each page's open thread counts.
  const pageCounts = data.items.flatMap((entry) => {
    const list = (threads.data ?? []).filter(
      (th) => th.itemId === entry.id && th.status === "open",
    );
    if (!list.length) {
      return [];
    }
    const by = (kind: string) => list.filter((th) => th.kind === kind).length;
    const words = [
      ...(by("change_request")
        ? [t("wiki.review.discussion.requests", { count: by("change_request") })]
        : []),
      ...(by("question") ? [t("wiki.review.discussion.questions", { count: by("question") })] : []),
      ...(by("comment") ? [t("wiki.review.discussion.comments", { count: by("comment") })] : []),
    ];
    return [{ entry, words: words.join(" · ") }];
  });
  return (
    <div data-esi-content="wiki" className="flex min-h-0 flex-1 flex-col">
      <header className="flex shrink-0 flex-col gap-2 border-b border-line px-4 py-3.5 sm:px-6">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <Link
            href={wikiChangesPath(project.id)}
            className="flex items-center gap-1 text-xs text-fg-secondary hover:text-fg"
          >
            <ArrowLeft aria-hidden="true" className="size-3.5" />
            {t("wiki.review.back")}
          </Link>
          <h2 className="min-w-0 flex-1 text-lg font-semibold tracking-tight break-words text-fg">
            {title}
          </h2>
          <span className="rounded-full border border-line px-2 py-0.5 text-xs text-fg-secondary">
            {t(`wiki.changes.state.${data.status}`)}
          </span>
          {canWrite && trust.decides ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon-sm" aria-label={t("wiki.review.summaryMenu")}>
                  <MoreHorizontal aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-44">
                {reopenable ? (
                  <DropdownMenuItem
                    disabled={action.isPending}
                    onSelect={() => action.mutate({ kind: "reopen", itemId: reopenable.id })}
                  >
                    <RotateCcw aria-hidden="true" />
                    {t("wiki.changes.reopen")}
                  </DropdownMenuItem>
                ) : null}
                <DropdownMenuItem
                  onSelect={() => {
                    setComment("");
                    setDialog("discard");
                  }}
                >
                  <X aria-hidden="true" />
                  {t("wiki.review.discard")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
        <p className="text-xs text-fg-secondary">
          {t(`wiki.changes.origin.${data.origin}`)} ·{" "}
          {data.provenance.createdByName ?? t("wiki.draft.provenance.someone")} ·{" "}
          {formatDateTime(data.submittedAt ?? data.createdAt)}
          {data.provenance.conversation && !data.hiddenItems ? (
            <>
              {" "}
              ·{" "}
              <Link
                href={projectConversationPath(project.id, data.provenance.conversation.id)}
                className="text-accent-text underline underline-offset-4"
              >
                {data.provenance.conversation.title ?? t("wiki.draft.provenance.untitled")}
              </Link>
            </>
          ) : null}
          {" · "}
          <span className="tabular-nums">{sizes}</span>
        </p>
        {data.rationale && !data.hiddenItems ? (
          <p className="max-w-prose text-sm break-words text-fg-secondary">{data.rationale}</p>
        ) : null}
        {data.provenance.mcp && !data.hiddenItems ? (
          <p className="text-xs text-fg-secondary">
            {data.provenance.mcp.clientHost} · {data.provenance.mcp.summary}
          </p>
        ) : null}
        {data.mcpPublisher && !closed ? (
          <p className="text-xs text-fg-secondary">
            {data.mcpPublisher === "anyone"
              ? t("wiki.changes.mcpPublisher.anyone")
              : mine
                ? t("wiki.changes.mcpPublisher.notYou")
                : t("wiki.changes.mcpPublisher.others", {
                    name: data.provenance.createdByName ?? t("wiki.draft.provenance.someone"),
                  })}
          </p>
        ) : null}
        {!data.hiddenItems &&
        !threads.isPending &&
        data.status === "changes_requested" &&
        data.reviewComment &&
        !(threads.data ?? []).some((th) => th.itemId === null && th.kind === "change_request") ? (
          // An N-1 API's send-back comment; threads carry it from this release on.
          <Notice tone="info" title={t("wiki.changes.state.changes_requested")}>
            {data.reviewComment} · {data.reviewCommentByName ?? ""}{" "}
            {data.reviewCommentAt ? formatDateTime(data.reviewCommentAt) : ""}
          </Notice>
        ) : null}
        {canWrite && !trust.decides && data.trust ? (
          // Who decides instead, where the decision bar would be (D6).
          <p className="text-xs text-fg-secondary">
            {t(`wiki.changes.trustRefused.${data.trust.level}`)}
          </p>
        ) : null}
        {working ? <Notice tone="info">{t("wiki.changes.workingOther")}</Notice> : null}
        {data.hiddenItems ? <Notice tone="warning">{t("wiki.changes.hidden")}</Notice> : null}
        {closed && data.publishedAt ? (
          <p className="text-xs text-fg-secondary">
            {t("wiki.changes.publishedBy", {
              name: data.publishedByName ?? t("wiki.draft.provenance.someone"),
              at: formatDateTime(data.publishedAt),
            })}
          </p>
        ) : null}
        {data.status === "published" ? (
          // Each published page links to the version this set wrote (read-only, D5).
          <ul
            aria-label={t("wiki.changes.publishedPages")}
            className="flex flex-wrap gap-x-4 gap-y-1 text-xs"
          >
            {data.items.flatMap((i) => {
              const doc = i.publishedDocumentId ?? i.pageId;
              return i.status === "published" && doc && i.publishedVersion && i.op !== "delete"
                ? [
                    <li key={i.id}>
                      <Link
                        href={wikiPath(project.id, doc, i.publishedVersion)}
                        className="text-accent-text underline underline-offset-4"
                      >
                        {t("wiki.changes.versionOf", {
                          title: i.title,
                          version: i.publishedVersion,
                        })}
                      </Link>
                    </li>,
                  ]
                : [];
            })}
          </ul>
        ) : null}
        {data.status === "discarded" && data.discardedAt ? (
          <div className="flex flex-col gap-1 text-xs text-fg-secondary">
            <p>
              {data.discardedBy
                ? t("wiki.changes.discardedBy", {
                    name: data.discardedByName ?? t("wiki.draft.provenance.someone"),
                    at: formatDateTime(data.discardedAt),
                  })
                : t("wiki.changes.discardedUnused", { at: formatDateTime(data.discardedAt) })}
            </p>
            {!data.hiddenItems &&
            data.reviewComment &&
            data.discardedBy &&
            data.reviewCommentBy?.toLowerCase() === data.discardedBy.toLowerCase() &&
            data.reviewCommentAt === data.discardedAt ? (
              <p className="max-w-prose break-words text-fg">
                <span className="sr-only">{t("wiki.inbox.closed.comment")} </span>
                <span data-esi-content="wiki">“{data.reviewComment}”</span>
              </p>
            ) : null}
          </div>
        ) : null}
        {data.status === "published" && !data.hiddenItems && canChange(project, "wiki.draft") ? (
          <div>
            <Button
              ref={revertTrigger}
              variant="outline"
              size="sm"
              onClick={() =>
                setRevert({
                  kind: "changeSet",
                  id,
                  title,
                  pages: data.items.filter((i) => i.status === "published").length,
                })
              }
            >
              <RotateCcw aria-hidden="true" />
              {t("wiki.restore.revert")}
            </Button>
          </div>
        ) : null}
        {commentDropped ? (
          <Notice tone="warning">{t("wiki.changes.discardCommentDropped")}</Notice>
        ) : null}
        {action.isError ? <Notice tone="danger">{action.error.message}</Notice> : null}
      </header>
      <div className="flex min-h-0 flex-1">
        <div className="flex min-w-0 flex-1 flex-col">
          {/* The reading area is the wide host (#379, D15): a wide table or code block breaks
              out of the 48rem column up to the area, which ends at the pane; Expand opens above
              the pane and the sheet. */}
          <div ref={scroller} className="wide-host min-h-0 flex-1 overflow-y-auto">
            <ReviewStrip
              set={data}
              tab={tab}
              onTab={setTab}
              discussionCount={setThreads.filter((th) => th.status === "open").length}
              selected={shownId ?? null}
              onSelect={select}
              flags={flagCount}
              decisions={decisions}
              onDecide={(decision, itemId) => action.mutate({ kind: decision, itemId })}
              busy={action.isPending}
              panelId={`${domId}-panel`}
              open={readable ? openItems.length : null}
              onStep={step}
              onOpenFirst={() => {
                const first = openItems[0];
                if (first) {
                  openEntry(first.key);
                }
              }}
              inline={inline}
              onInline={(on) => setInlinePref(on ? "on" : "off")}
              stepper={layout === "side"}
              since={
                sinceReview.data?.items.length ? { on: sinceOn, onChange: setSinceOn } : undefined
              }
            />
            <div
              id={`${domId}-panel`}
              role="tabpanel"
              data-review-body=""
              className="mx-auto flex max-w-[48rem] flex-col gap-4 px-4 py-5 sm:px-8"
            >
              {tab === "discussion" ? (
                <section
                  aria-label={t("wiki.review.strip.discussion")}
                  className="flex flex-col gap-5"
                >
                  <ThreadPanel
                    projectId={project.id}
                    set={data}
                    itemId={null}
                    canComment={canComment}
                  />
                  {pageCounts.length ? (
                    <section className="flex flex-col gap-2">
                      <h3 className="text-xs font-medium text-fg-muted">
                        {t("wiki.review.discussion.onPages")}
                      </h3>
                      <ul className="divide-y divide-line rounded-lg border border-line bg-surface-raised">
                        {pageCounts.map(({ entry, words }) => (
                          <li key={entry.id}>
                            <button
                              type="button"
                              onClick={() => {
                                const target =
                                  outlineGroupOf(entry) === "structure" ? STRUCTURE : entry.id;
                                navigate(changeSetPath(project.id, id, target));
                              }}
                              className="flex w-full flex-col gap-0.5 px-3.5 py-2.5 text-left hover:bg-surface-hover focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                            >
                              <span className="text-sm font-medium text-fg">{itemName(entry)}</span>
                              <span className="text-xs text-fg-secondary">{words}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </section>
                  ) : null}
                </section>
              ) : shownId === STRUCTURE ? (
                <section aria-label={t("wiki.review.outline.tree")} className="flex flex-col gap-3">
                  <h3 className="text-lg font-semibold tracking-tight text-fg">
                    {t("wiki.review.outline.tree")}
                  </h3>
                  <p className="text-xs text-fg-secondary">{t("wiki.review.structureNote")}</p>
                  <ul className="flex flex-col gap-3">
                    {structure.map((entry) => (
                      <li key={entry.id} className="flex flex-col gap-1.5">
                        <p className="flex flex-wrap items-center gap-x-2 text-sm font-medium break-words text-fg">
                          {itemName(entry)}
                          <span className="text-xs font-normal text-fg-secondary">
                            {itemFacts(entry, working)}
                          </span>
                        </p>
                        <ItemContent projectId={project.id} item={entry} />
                        {/* Each move or folder keeps its own discussion (a change request on it
                            blocks publication and is resolved here). */}
                        <ThreadPanel
                          projectId={project.id}
                          set={data}
                          itemId={entry.id}
                          canComment={canComment}
                        />
                        {decisions(entry).length ? (
                          <div className="flex gap-1.5">
                            {decisions(entry).map((decision) => (
                              <Button
                                key={decision}
                                size="xs"
                                variant="ghost"
                                disabled={action.isPending}
                                onClick={() => action.mutate({ kind: decision, itemId: entry.id })}
                              >
                                {t(`wiki.changes.${decision}`)}
                                <span className="sr-only">: {itemName(entry)}</span>
                              </Button>
                            ))}
                          </div>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </section>
              ) : item.isError ? (
                <Notice tone="danger">{item.error.message}</Notice>
              ) : !item.data ? (
                <Skeleton className="h-48" />
              ) : (
                <section aria-label={itemName(item.data)} className="flex flex-col gap-3">
                  <h3
                    data-selection-ignore=""
                    className="text-xl font-semibold tracking-tight break-words text-fg"
                  >
                    {itemName(item.data)}
                  </h3>
                  <p data-selection-ignore="" className="text-xs text-fg-secondary">
                    {itemFacts(item.data, working)}
                    {itemSize(item.data) ? ` · ${itemSize(item.data)}` : ""}
                    {isFolderOp(item.data.op)
                      ? null
                      : ` · ${t(`wiki.docs.trustValues.${item.data.trust}`)}`}
                    {changeRequests.onItem(item.data.id) ? (
                      <span className="text-warning-fg">
                        {" "}
                        · {t("wiki.changes.changesRequested")}
                      </span>
                    ) : null}
                    {item.data.approvedBy
                      ? ` · ${t("wiki.changes.approvedBy", { name: item.data.approvedByName ?? t("wiki.draft.provenance.someone") })}`
                      : ""}
                    {item.data.rejectedBy
                      ? ` · ${t("wiki.changes.rejectedBy", { name: item.data.rejectedByName ?? t("wiki.draft.provenance.someone") })}`
                      : ""}
                  </p>
                  {/* What the page draws on; a deletion's only source is the page itself. */}
                  {isFolderOp(item.data.op) || item.data.op === "delete" ? null : (
                    <p data-selection-ignore="" className="text-xs text-fg-secondary">
                      <span className="text-fg-muted">{t("wiki.changes.sources")}: </span>
                      {item.data.sources.length
                        ? item.data.sources
                            .map((source) => source.title ?? t("wiki.draft.sourceDeleted"))
                            .join(" · ")
                        : t("wiki.changes.noSources")}
                    </p>
                  )}
                  {item.data.rebasedFromVersion ? (
                    <Notice>
                      {t("wiki.changes.rebased", { version: item.data.rebasedFromVersion })}
                    </Notice>
                  ) : null}
                  {item.data.staleReason ? (
                    <Notice tone="warning">
                      {isFolderOp(item.data.op)
                        ? t("wiki.changes.folderMissing")
                        : t(`wiki.changes.stale.${item.data.staleReason}`)}
                    </Notice>
                  ) : null}
                  <ItemWarnings warnings={item.data.warnings} />
                  {canWrite && !editing ? (
                    <div data-selection-ignore="" className="flex flex-wrap gap-1.5">
                      {["edit", "create", "rename"].includes(item.data.op) ? (
                        <Button variant="ghost" size="xs" onClick={() => setEditing(true)}>
                          {t("wiki.changes.edit")}
                        </Button>
                      ) : null}
                      {item.data.status === "stale" && item.data.op === "edit" ? (
                        <Button
                          variant="ghost"
                          size="xs"
                          disabled={action.isPending}
                          onClick={() =>
                            action.mutate({ kind: "regenerate", itemId: item.data?.id })
                          }
                        >
                          {t("wiki.changes.regenerate")}
                        </Button>
                      ) : null}
                    </div>
                  ) : null}
                  {canWrite && rejected === item.data.id && item.data.status === "rejected" ? (
                    <DismissReasons
                      key={item.data.id}
                      onReason={async (reason) =>
                        noContent(
                          await endpoint.items[":itemId"].reject.$post(
                            { param: { ...param, itemId: item.data.id } },
                            csrfJson(csrfToken, { reason }),
                          ),
                        )
                      }
                    >
                      {t("recommendations.itemRejected")}
                    </DismissReasons>
                  ) : null}
                  {editing ? (
                    <ChangeItemEditor
                      key={item.data.id}
                      project={project}
                      item={item.data}
                      id={id}
                      onTextChange={setUnsavedText}
                      onDirty={setDirty}
                      onDone={() => {
                        setEditing(false);
                        setDirty(false);
                        void refresh();
                      }}
                      onCancel={() =>
                        guard.confirm(() => {
                          setEditing(false);
                          setDirty(false);
                        })
                      }
                    />
                  ) : readable ? (
                    <div ref={page} data-reading-page="" className="relative">
                      <ReviewHighlights
                        container={page}
                        entries={placed}
                        open={currentAnchor}
                        version={`${item.data.id}:${view}:${item.data.updatedAt}:${inline}:${reviewedBase !== null}`}
                      />
                      <MarkedPage
                        base={baseMarkdown}
                        baseLabel={
                          reviewedBase !== null ? t("wiki.history.review.against") : undefined
                        }
                        proposed={item.data.markdown ?? ""}
                        baseVersion={item.data.baseVersion}
                        title={item.data.title}
                        view={view}
                        onView={setView}
                        headingPrefix={headingPrefix}
                        inlineAt={inlineAt}
                        blockAfter={blockNodes}
                      >
                        <SinceReviewLine item={sinceItem} />
                        {unplaced.length ? (
                          <div data-selection-ignore="" className="flex flex-col gap-1.5">
                            {unplacedLines.length ? (
                              <IssueLines>{unplacedLines.map(line)}</IssueLines>
                            ) : null}
                            {unplacedMarkers.length ? (
                              <p className="flex flex-wrap items-center gap-1 text-xs text-fg-muted">
                                {t("wiki.review.onPage")}
                                {unplacedMarkers.map(marker)}
                              </p>
                            ) : null}
                          </div>
                        ) : null}
                      </MarkedPage>
                      {canComment ? (
                        <PageComment
                          // A draft belongs to its page: switching pages starts afresh.
                          key={`comment-${item.data.id}`}
                          agentEligible={agentEligible}
                          busy={write.isPending}
                          onPost={(kind, body, forAgent) =>
                            postThread({
                              kind: "open",
                              request: {
                                itemId: item.data?.id ?? "",
                                kind,
                                body,
                                ...(forAgent && { forAgent: true }),
                              },
                            })
                          }
                        />
                      ) : null}
                      {canComment ? (
                        <SelectionPopover
                          key={item.data.id}
                          mode="comment"
                          container={page}
                          agentEligible={agentEligible}
                          onComment={(passage, kind, body, forAgent) =>
                            postThread({
                              kind: "open",
                              request: {
                                itemId: item.data?.id ?? "",
                                kind,
                                body,
                                ...(forAgent && { forAgent: true }),
                                anchor: {
                                  quote: passage.quote,
                                  prefix: passage.prefix,
                                  suffix: passage.suffix,
                                  section: sectionSlug(passage, headingPrefix),
                                },
                              },
                            })
                          }
                        />
                      ) : null}
                    </div>
                  ) : (
                    <>
                      <ItemContent projectId={project.id} item={item.data} />
                      <ThreadPanel
                        projectId={project.id}
                        set={data}
                        itemId={item.data.id}
                        canComment={canComment}
                      />
                    </>
                  )}
                  {write.isError ? <Notice tone="danger">{write.error.message}</Notice> : null}
                  {item.data.publishedDocumentId && item.data.publishedVersion ? (
                    <Link
                      className="text-sm text-accent-text hover:underline"
                      href={wikiPath(
                        project.id,
                        item.data.publishedDocumentId,
                        item.data.publishedVersion,
                      )}
                    >
                      {t("wiki.changes.openPublished")}
                    </Link>
                  ) : null}
                </section>
              )}
            </div>
          </div>
          {layout === "side" ? bar : null}
        </div>
        {layout === "side" && current && paneItem ? (
          <ReviewPane {...paneItem} {...frame} dock="side" />
        ) : null}
      </div>
      {layout === "sheet" ? (
        <ReviewSheet
          {...(paneItem ?? {})}
          {...frame}
          entry={current}
          dock="bottom"
          openCount={tab === "changes" && readable ? openItems.length : 0}
          onOpenFirst={() => {
            const first = openItems[0];
            if (first) {
              openEntry(first.key);
            }
          }}
          expanded={sheetOpen}
          onExpanded={setSheetOpen}
          bar={decides || ink || menu ? bar : null}
        />
      ) : null}
      <AppDialog
        open={answering}
        onClose={() => {
          if (!resolveAnswered.isPending) {
            setAnswering(false);
          }
        }}
        title={t("wiki.resolve.bar.resolveTitle", { count: answered.length })}
        description={t("wiki.resolve.bar.resolveBody")}
      >
        <ResolveStep
          busy={resolveAnswered.isPending}
          onCancel={() => setAnswering(false)}
          onResolve={async (note) => {
            await resolveAnswered.mutateAsync({ threadIds: answered.map((th) => th.id), note });
            setAnswering(false);
          }}
        />
        {resolveAnswered.isError ? (
          <Notice tone="danger">{resolveAnswered.error.message}</Notice>
        ) : null}
      </AppDialog>
      {resolveAnswered.isSuccess && resolveAnswered.data.resolved.length ? (
        <p role="status" className="sr-only">
          {t("wiki.resolve.bar.resolved", { count: resolveAnswered.data.resolved.length })}
        </p>
      ) : null}
      <RestoreDialog
        projectId={project.id}
        target={revert}
        onClose={() => setRevert(null)}
        returnFocus={() => revertTrigger.current}
      />
      <AppDialog
        open={dialog === "request" || dialog === "discard"}
        onClose={() => setDialog(null)}
        title={dialog === "request" ? t("wiki.review.bar.request") : t("wiki.review.discard")}
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={async (e) => {
            e.preventDefault();
            if (dialog === "discard") {
              action.mutate({ kind: "discard" });
              return;
            }
            // A set-level change request (D6): the server sends a submitted set back with it.
            try {
              await write.mutateAsync({
                kind: "open",
                request: { kind: "change_request", body: comment.trim() },
              });
              setDialog(null);
              setComment("");
              void refresh();
            } catch {
              // Shown below.
            }
          }}
        >
          {dialog === "request" ? (
            <>
              <p className="text-sm text-fg-secondary">{t("wiki.review.requestBody")}</p>
              <Label htmlFor={`${domId}-request`}>{t("wiki.review.requestLabel")}</Label>
              <Textarea
                id={`${domId}-request`}
                required
                maxLength={2000}
                value={comment}
                onChange={(e) => setComment(e.currentTarget.value)}
              />
            </>
          ) : (
            <>
              <p className="text-sm text-fg-secondary">{t("wiki.changes.discardBody")}</p>
              <Label htmlFor={`${domId}-discard`}>{t("wiki.changes.discardComment")}</Label>
              <Textarea
                id={`${domId}-discard`}
                maxLength={2000}
                value={comment}
                onChange={(e) => setComment(e.currentTarget.value)}
              />
            </>
          )}
          {dialog === "request" && write.isError ? (
            <Notice tone="danger">{write.error.message}</Notice>
          ) : null}
          {dialog === "discard" && action.isError ? (
            <Notice tone="danger">{action.error.message}</Notice>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDialog(null)}>
              {t("wiki.editor.cancel")}
            </Button>
            <Button
              type="submit"
              variant={dialog === "discard" ? "destructive" : "default"}
              disabled={
                action.isPending || write.isPending || (dialog === "request" && !comment.trim())
              }
            >
              {dialog === "request" ? t("wiki.review.bar.request") : t("wiki.review.discard")}
            </Button>
          </DialogFooter>
        </form>
      </AppDialog>
      <ChangeSetPublishDialog
        open={dialog === "publish"}
        onClose={() => setDialog(null)}
        project={project}
        changeSet={data}
      />
      {status ? (
        <span id={`${domId}-status`} className="sr-only">
          {status}
        </span>
      ) : null}
      {guard.dialog}
    </div>
  );
}

/** One cached computation per review: the reading view's diff and placements, per render. */
function once<A extends unknown[], R>(compute: (...args: A) => R): (...args: A) => R {
  // Strings (the page's Markdown) compare by value without being serialised; the small
  // arguments (anchors, struck ranges) by their JSON.
  const keyOf = (args: A) => args.map((a) => (typeof a === "string" ? a : JSON.stringify(a)));
  let last: { key: string[]; value: R } | null = null;
  return (...args: A) => {
    const key = keyOf(args);
    if (!last || last.key.length !== key.length || last.key.some((k, i) => k !== key[i])) {
      last = { key, value: compute(...args) };
    }
    return last.value;
  };
}
const shownPageOnce = once(shownPage);
const placeAnchorsOnce = once(placeAnchors);

/**
 * A comment on the page as a whole, out of the flow until asked for (D15: no forms in the text);
 * the new thread opens in the pane.
 */
function PageComment(props: {
  agentEligible: boolean;
  busy: boolean;
  onPost: (kind: WikiThreadKind, body: string, forAgent: boolean) => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const [composing, setComposing] = useState(false);
  return (
    <div data-selection-ignore="" className="mt-6 border-t border-line pt-4">
      {composing ? (
        <NewThread
          label={t("wiki.threads.bodyItem")}
          agentEligible={props.agentEligible}
          busy={props.busy}
          onPost={async (kind, body, forAgent) => {
            await props.onPost(kind, body, forAgent);
            setComposing(false);
          }}
        />
      ) : (
        <Button size="xs" variant="ghost" onClick={() => setComposing(true)}>
          <MessageSquare aria-hidden="true" />
          {t("wiki.threads.bodyItem")}
        </Button>
      )}
    </div>
  );
}

/** Tints the passages with a thread or a flag and rings the open one (D15). */
function ReviewHighlights(props: {
  container: RefObject<HTMLElement | null>;
  entries: readonly { entry: ReviewEntry; at: Placement | null }[];
  open: PassageAnchor | null;
  version: unknown;
}) {
  const threads: PassageAnchor[] = [];
  const flags: PassageAnchor[] = [];
  for (const { entry, at } of props.entries) {
    const anchor = anchorOf(entry);
    if (anchor && at) {
      (entry.type === "flag" ? flags : threads).push(anchor);
    }
  }
  usePassageHighlights(props.container, { threads, flags }, props.open, props.version);
  return null;
}

export const folderPathText = (path: readonly string[] | null | undefined) =>
  (path ?? []).join(" / ");

/** An item's name in lists: a folder item by its path, a page by its title. */
export function itemName(item: Pick<WikiChangeItem, "op" | "title" | "folderPath">): string {
  return isFolderOp(item.op) && item.folderPath?.length
    ? folderPathText(item.folderPath)
    : item.title;
}

/**
 * An item's facts line: "Folder · New folder · Pending", or the page's operation and status,
 * with "Not checked" and its reason when the submission check could not run (D7).
 */
export function itemFacts(
  item: Pick<WikiChangeItem, "op" | "status" | "contradictionCheck" | "contradictionCheckReason">,
  working: boolean,
): string {
  const t = i18nT;
  const facts = [
    ...(isFolderOp(item.op) ? [t("wiki.changes.folderItem")] : []),
    t(`wiki.changes.op.${item.op}`),
    t(`wiki.changes.itemState.${item.status}`),
  ];
  if (!isFolderOp(item.op) && (item.op === "create" || item.op === "edit")) {
    if (item.contradictionCheckReason) {
      facts.push(t(`wiki.changes.notCheckedReason.${item.contradictionCheckReason}`));
    } else if (item.contradictionCheck === "not_checked") {
      facts.push(t(working ? "wiki.changes.notCheckedWorking" : "wiki.changes.notChecked"));
    }
  }
  return facts.join(" · ");
}

export function ItemContent({ projectId, item }: { projectId: string; item: WikiChangeItem }) {
  const { t } = useTranslation();
  const [source, setSource] = useState(false);
  const folders = useFolders(projectId);
  const page = useWikiPage(
    projectId,
    item.pageId ?? "",
    item.baseVersion ?? undefined,
    !!item.pageId && (item.op === "move" || item.op === "rename"),
  );
  const folder = (id: string | null) =>
    folders.data?.folders.find((f) => f.id === id)?.name ?? t("wiki.changes.root");
  // A page placed by path names a folder the change set creates (resolved at publication).
  const target = item.folderPath?.length ? folderPathText(item.folderPath) : folder(item.folderId);
  if (isFolderOp(item.op)) {
    return (
      <Notice>
        {t(`wiki.changes.folder.${item.op}`, {
          path: folderPathText(item.folderPath),
          name: item.folderNewName ?? "",
          target: item.folderTargetPath?.length
            ? folderPathText(item.folderTargetPath)
            : t("wiki.changes.root"),
        })}
      </Notice>
    );
  }
  if (item.op === "move") {
    return (
      <Notice>
        {page.data ? (
          <p>{t("wiki.changes.moveFrom", { folder: folder(page.data.folderId ?? null) })}</p>
        ) : null}
        {t("wiki.changes.moveTo", { folder: target })}
      </Notice>
    );
  }
  if (item.op === "delete") {
    return (
      <>
        <Notice tone="warning">{t("wiki.changes.deleting")}</Notice>
        <Markdown markdown={item.baseMarkdown ?? item.markdown ?? ""} />
      </>
    );
  }
  if (item.op === "edit" || item.op === "rename") {
    return (
      <>
        <p className="text-xs text-fg-muted">
          {page.data ? `${page.data.title} → ` : ""}
          {t("wiki.changes.proposedTitle", { title: item.title })}
        </p>
        <DiffView
          before={item.baseMarkdown ?? ""}
          after={item.markdown ?? ""}
          version={item.baseVersion ?? 1}
        />
      </>
    );
  }
  return (
    <>
      {item.folderPath?.length ? (
        <p className="text-xs text-fg-muted">
          {t("wiki.changes.inFolder", { path: folderPathText(item.folderPath) })}
        </p>
      ) : null}
      <Button
        variant="ghost"
        size="sm"
        className="self-start"
        onClick={() => setSource(!source)}
        aria-pressed={source}
      >
        {t(source ? "wiki.changes.rendered" : "wiki.changes.source")}
      </Button>
      {source ? (
        <pre className="whitespace-pre-wrap break-words text-sm">{item.markdown}</pre>
      ) : (
        <Markdown markdown={item.markdown ?? ""} />
      )}
    </>
  );
}

export function ChangeItemEditor({
  project,
  item,
  id,
  onDirty,
  onTextChange,
  onDone,
  onCancel,
}: {
  project: ProjectDetail;
  item: WikiChangeItem;
  id: string;
  onDirty: (value: boolean) => void;
  onTextChange: (text: string) => void;
  onDone: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const uid = useId();
  const { csrfToken } = useSession();
  const [title, setTitle] = useState(item.title);
  const [markdown, setMarkdown] = useState(item.markdown ?? "");
  const [pageType, setPageType] = useState<WikiPageType>(item.pageType);
  const [folderId, setFolderId] = useState<FolderId | null>(item.folderId as FolderId | null);
  const current = useWikiPage(
    project.id,
    item.pageId ?? "",
    undefined,
    item.status === "stale" && !!item.pageId,
  );
  const dirty =
    title !== item.title ||
    markdown !== (item.markdown ?? "") ||
    pageType !== item.pageType ||
    folderId !== item.folderId;
  useEffect(() => {
    onDirty(dirty);
    onTextChange(`${title}\n\n${markdown}`);
  }, [dirty, onDirty, onTextChange, title, markdown]);
  const save = useMutation({
    mutationFn: async () => {
      const json: UpdateWikiChangeItemRequest = {
        title,
        pageType,
        ...(item.op !== "rename" ? { markdown } : {}),
        ...(item.op === "create" ? { folderId } : {}),
        ...(item.status === "stale" && current.data
          ? { baseVersion: current.data.shownVersion }
          : {}),
      };
      return ok(
        await api.api.projects[":projectId"].wiki["change-sets"][":id"].items[":itemId"].$patch(
          { param: { projectId: project.id, id, itemId: item.id }, json },
          { headers: { [CSRF_HEADER]: csrfToken } },
        ),
      );
    },
    onSuccess: onDone,
  });
  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      {item.approvedBy ? <Notice tone="warning">{t("wiki.changes.clearsApproval")}</Notice> : null}
      {item.status === "stale" ? <Notice>{t("wiki.changes.reconcile")}</Notice> : null}
      <Label htmlFor={`${uid}-title`}>{t("wiki.editor.title")}</Label>
      <Input
        id={`${uid}-title`}
        required
        maxLength={200}
        value={title}
        onChange={(e) => setTitle(e.currentTarget.value)}
      />
      <Label htmlFor={`${uid}-type`}>{t("wiki.editor.pageType")}</Label>
      <NativeSelect
        id={`${uid}-type`}
        value={pageType}
        onChange={(e) => setPageType(e.currentTarget.value as WikiPageType)}
      >
        {(["summary", "concept", "entity", "other"] as const).map((type) => (
          <option key={type} value={type}>
            {t(`wiki.pageTypes.${type}`)}
          </option>
        ))}
      </NativeSelect>
      {item.op !== "rename" ? (
        <>
          <Label htmlFor={`${uid}-markdown`}>{t("wiki.editor.markdown")}</Label>
          <Textarea
            id={`${uid}-markdown`}
            className="min-h-64 font-mono text-sm"
            value={markdown}
            maxLength={200000}
            onChange={(e) => setMarkdown(e.currentTarget.value)}
          />
          <details open>
            <summary className="mb-3 text-sm font-medium">{t("wiki.editor.preview")}</summary>
            <Markdown markdown={markdown} />
          </details>
        </>
      ) : null}
      {item.op === "create" ? (
        <FolderField
          projectId={project.id}
          section="wiki"
          value={folderId}
          onChange={setFolderId}
          canCreate={canChange(project, "folder.manage")}
        />
      ) : null}
      {item.status === "stale" && current.data ? (
        <details>
          <summary>{t("wiki.changes.before")}</summary>
          <Markdown markdown={current.data.markdown} />
        </details>
      ) : null}
      {current.isError ? <Notice tone="danger">{current.error.message}</Notice> : null}
      {save.isError ? <Notice tone="danger">{save.error.message}</Notice> : null}
      <div className="sticky bottom-0 flex gap-2 border-t border-line bg-surface py-3">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t("wiki.editor.cancel")}
        </Button>
        <Button
          type="submit"
          disabled={save.isPending || !title.trim() || (item.status === "stale" && !current.data)}
        >
          {t("wiki.changes.save")}
        </Button>
      </div>
    </form>
  );
}
